import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { queueApi } from '../../data/api/queue.api'
import type { ListParams } from '../../data/types/ListParams'
import { PENDING_STATUSES } from '../enums/ActivityStatus'
import type { QueueActivity } from '../models/QueueActivity'

export const queueKeys = {
  all: ['queue'] as const,
  list: (params: ListParams) => ['queue', params] as const,
  count: (filter?: Record<string, unknown>) => ['queue', 'count', { filter }] as const,
  detail: (id: string | undefined) => ['queue', 'detail', id] as const,
}

export function useQueueActivities(params: ListParams) {
  return useQuery({
    queryKey: queueKeys.list(params),
    queryFn: () => queueApi.list(params.skip, params.top, params.filter).then((r) => r.data),
    refetchInterval: (query) =>
      query.state.data?.some((activity) => PENDING_STATUSES.some((s) => s === activity.status))
        ? 5000
        : false,
  })
}

export function useQueueActivitiesCount(params: ListParams) {
  const qc = useQueryClient()
  return useQuery({
    queryKey: queueKeys.count(params.filter),
    queryFn: () => queueApi.count(params.filter).then((r) => r.data),
    refetchInterval: () => {
      const cached = qc.getQueryData<QueueActivity[]>(queueKeys.list(params))
      return cached?.some((activity) => PENDING_STATUSES.some((s) => s === activity.status))
        ? 5000
        : false
    },
  })
}

export function useQueueActivity(id: string | undefined) {
  return useQuery({
    queryKey: queueKeys.detail(id),
    queryFn: id ? () => queueApi.detail(id).then((r) => r.data) : skipToken,
  })
}

export function useQueueActivityDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => queueApi.delete(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKeys.all }),
  })
}

export function useQueueActivityCancel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (activityId: string) => queueApi.cancel(activityId).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queueKeys.all }),
  })
}
